import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';

function buildVendorKey(vendorType, vendorId) {
  return `${String(vendorType || '').trim()}__${String(vendorId || '').trim()}`;
}

function AdminRazorpayPanel() {
  const adminToken = localStorage.getItem('adminToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorKey, setSelectedVendorKey] = useState('');
  const [statusRow, setStatusRow] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [stakeholderForm, setStakeholderForm] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: 'Thane',
    state: 'Maharashtra',
    postal_code: '401101',
    country: 'IN',
    pan: '',
  });

  const [productUpdateForm, setProductUpdateForm] = useState({
    account_number: '',
    ifsc_code: '',
    beneficiary_name: '',
  });

  const axiosConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
      },
    }),
    [adminToken]
  );

  const getErrorMessage = (err, fallback = 'Something went wrong.') =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const normalizeVendorList = (list = []) => {
    return list.map((vendor) => ({
      ...vendor,
      vendorType: String(vendor.vendorType || '').trim(),
      vendorKey:
        vendor.vendorKey ||
        buildVendorKey(vendor.vendorType, vendor.id),
    }));
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${apiUrl}/admin/vendors`, axiosConfig);
      const list = res?.data?.vendors || res?.data?.data || [];
      setVendors(normalizeVendorList(list));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load vendors.'));
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorStatus = async (vendorId, vendorType) => {
    try {
      setLoading(true);
      setError('');

      const res = await axios.get(
        `${apiUrl}/admin/vendor-onboarding/status?vendorId=${encodeURIComponent(
          vendorId
        )}&vendorType=${encodeURIComponent(vendorType)}`,
        axiosConfig
      );

      const row = res?.data?.row || null;
      setStatusRow(row);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch vendor status.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      fetchVendors();
    }
  }, [adminToken]);

  const handleVendorChange = (vendorKey) => {
    setSelectedVendorKey(vendorKey);
    setMessage('');
    setError('');
    setStatusRow(null);

    const vendor = vendors.find((v) => String(v.vendorKey) === String(vendorKey));
    setSelectedVendor(vendor || null);

    if (vendor) {
      setStakeholderForm((prev) => ({
        ...prev,
        name: vendor.name || '',
        email: vendor.email || '',
        phone: vendor.number || '',
        street: vendor.liveLocation || vendor.shopName || '',
      }));

      setProductUpdateForm((prev) => ({
        ...prev,
        beneficiary_name: vendor.name || vendor.shopName || '',
      }));

      fetchVendorStatus(vendor.id, vendor.vendorType);
    }
  };

  const ensureVendorSelected = () => {
    if (!selectedVendor?.id || !selectedVendor?.vendorType) {
      throw new Error('Please select a vendor first.');
    }
  };

  const handleLinkedAccountInit = async () => {
    try {
      ensureVendorSelected();
      setLoading(true);
      setError('');
      setMessage('');

      await axios.post(
        `${apiUrl}/admin/vendor-onboarding/linked-account/init`,
        {
          vendorId: selectedVendor.id,
          vendorType: selectedVendor.vendorType,
        },
        axiosConfig
      );

      setMessage('Linked account created successfully.');
      await fetchVendorStatus(selectedVendor.id, selectedVendor.vendorType);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create linked account.'));
    } finally {
      setLoading(false);
    }
  };

  const handleStakeholderCreate = async () => {
    try {
      ensureVendorSelected();
      setLoading(true);
      setError('');
      setMessage('');

      await axios.post(
        `${apiUrl}/admin/vendor-onboarding/stakeholder/upsert`,
        {
          vendorId: selectedVendor.id,
          vendorType: selectedVendor.vendorType,
          stakeholderPayload: {
            name: stakeholderForm.name,
            email: stakeholderForm.email,
            percentage_ownership: 100,
            relationship: {
              executive: true,
              director: false,
            },
            phone: {
              primary: Number(String(stakeholderForm.phone).replace(/\D/g, '')),
            },
            addresses: {
              residential: {
                street: stakeholderForm.street,
                city: stakeholderForm.city,
                state: stakeholderForm.state,
                postal_code: stakeholderForm.postal_code,
                country: stakeholderForm.country,
              },
            },
            kyc: {
              pan: stakeholderForm.pan,
            },
            notes: {
              vendorId: String(selectedVendor.id),
              vendorType: String(selectedVendor.vendorType),
            },
          },
        },
        axiosConfig
      );

      setMessage('Stakeholder created successfully.');
      await fetchVendorStatus(selectedVendor.id, selectedVendor.vendorType);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create stakeholder.'));
    } finally {
      setLoading(false);
    }
  };

  const handleInitProduct = async () => {
    try {
      ensureVendorSelected();
      setLoading(true);
      setError('');
      setMessage('');

      await axios.post(
        `${apiUrl}/admin/vendor-onboarding/route-product/init`,
        {
          vendorId: selectedVendor.id,
          vendorType: selectedVendor.vendorType,
          productPayload: {
            product_name: 'route',
            tnc_accepted: true,
          },
        },
        axiosConfig
      );

      setMessage('Product configuration initialized successfully.');
      await fetchVendorStatus(selectedVendor.id, selectedVendor.vendorType);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to init product configuration.'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    try {
      ensureVendorSelected();
      setLoading(true);
      setError('');
      setMessage('');

      await axios.post(
        `${apiUrl}/admin/vendor-onboarding/route-product/update`,
        {
          vendorId: selectedVendor.id,
          vendorType: selectedVendor.vendorType,
          productConfigPayload: {
            settlements: {
              account_number: productUpdateForm.account_number,
              ifsc_code: productUpdateForm.ifsc_code,
              beneficiary_name: productUpdateForm.beneficiary_name,
            },
          },
        },
        axiosConfig
      );

      setMessage('Product configuration updated successfully.');
      await fetchVendorStatus(selectedVendor.id, selectedVendor.vendorType);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update product configuration.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      ensureVendorSelected();
      setLoading(true);
      setError('');
      setMessage('');

      await axios.post(
        `${apiUrl}/admin/vendor-onboarding/sync`,
        {
          vendorId: selectedVendor.id,
          vendorType: selectedVendor.vendorType,
        },
        axiosConfig
      );

      setMessage('Razorpay status synced successfully.');
      await fetchVendorStatus(selectedVendor.id, selectedVendor.vendorType);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to sync status.'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.href = '/admin/login';
  };

  if (!adminToken) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2>Admin access required</h2>
          <p>Please login first.</p>
          <a href="/admin/login">Go to Admin Login</a>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Admin Razorpay Panel</h1>
            <p style={styles.subtitle}>
              Approved admin hi yeh panel access kar sakta hai.
            </p>
          </div>

          <div style={styles.topRight}>
            <span style={styles.adminInfo}>
              {adminUser?.name || 'Admin'} ({adminUser?.email || 'No Email'})
            </span>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              Logout
            </button>
          </div>
        </div>

        {message ? <div style={styles.success}>{message}</div> : null}
        {error ? <div style={styles.error}>{error}</div> : null}

        <div style={styles.section}>
          <label style={styles.label}>Select Vendor</label>
          <select
            value={selectedVendorKey}
            onChange={(e) => handleVendorChange(e.target.value)}
            style={styles.input}
          >
            <option value="">-- Select Vendor --</option>
            {vendors.map((vendor) => (
              <option key={vendor.vendorKey} value={vendor.vendorKey}>
                {vendor.name} ({vendor.vendorType}) - ID: {vendor.id}
              </option>
            ))}
          </select>
        </div>

        {selectedVendor && (
          <>
            <div style={styles.section}>
              <h3>Vendor Details</h3>
              <div style={styles.grid2}>
                <div><strong>Name:</strong> {selectedVendor.name}</div>
                <div><strong>Email:</strong> {selectedVendor.email}</div>
                <div><strong>Phone:</strong> {selectedVendor.number}</div>
                <div><strong>Vendor Type:</strong> {selectedVendor.vendorType}</div>
                <div><strong>Vendor ID:</strong> {selectedVendor.id}</div>
                <div><strong>Shop:</strong> {selectedVendor.shopName}</div>
                <div><strong>Location:</strong> {selectedVendor.liveLocation}</div>
              </div>
            </div>

            <div style={styles.actions}>
              <button onClick={handleLinkedAccountInit} style={styles.button} disabled={loading}>
                1. Create Linked Account
              </button>
              <button onClick={handleStakeholderCreate} style={styles.button} disabled={loading}>
                2. Create Stakeholder
              </button>
              <button onClick={handleInitProduct} style={styles.button} disabled={loading}>
                3. Init Product Config
              </button>
              <button onClick={handleUpdateProduct} style={styles.button} disabled={loading}>
                4. Update Product Config
              </button>
              <button onClick={handleSync} style={styles.button} disabled={loading}>
                5. Sync Status
              </button>
            </div>

            <div style={styles.section}>
              <h3>Stakeholder Form</h3>
              <div style={styles.grid2}>
                <input
                  style={styles.input}
                  placeholder="Name"
                  value={stakeholderForm.name}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="Email"
                  value={stakeholderForm.email}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="Phone"
                  value={stakeholderForm.phone}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({ ...prev, phone: e.target.value }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="PAN"
                  value={stakeholderForm.pan}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({
                      ...prev,
                      pan: e.target.value.toUpperCase(),
                    }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="Street"
                  value={stakeholderForm.street}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({ ...prev, street: e.target.value }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="City"
                  value={stakeholderForm.city}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({ ...prev, city: e.target.value }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="State"
                  value={stakeholderForm.state}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({ ...prev, state: e.target.value }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="Postal Code"
                  value={stakeholderForm.postal_code}
                  onChange={(e) =>
                    setStakeholderForm((prev) => ({
                      ...prev,
                      postal_code: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div style={styles.section}>
              <h3>Product Update Form</h3>
              <div style={styles.grid2}>
                <input
                  style={styles.input}
                  placeholder="Bank Account Number"
                  value={productUpdateForm.account_number}
                  onChange={(e) =>
                    setProductUpdateForm((prev) => ({
                      ...prev,
                      account_number: e.target.value,
                    }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="IFSC Code"
                  value={productUpdateForm.ifsc_code}
                  onChange={(e) =>
                    setProductUpdateForm((prev) => ({
                      ...prev,
                      ifsc_code: e.target.value.toUpperCase(),
                    }))
                  }
                />
                <input
                  style={styles.input}
                  placeholder="Beneficiary Name"
                  value={productUpdateForm.beneficiary_name}
                  onChange={(e) =>
                    setProductUpdateForm((prev) => ({
                      ...prev,
                      beneficiary_name: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div style={styles.section}>
              <h3>Current Razorpay Status</h3>
              <pre style={styles.pre}>{JSON.stringify(statusRow, null, 2)}</pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f4f6f8',
    padding: '20px',
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    background: '#fff',
    borderRadius: '14px',
    padding: '24px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  },
  card: {
    maxWidth: '420px',
    margin: '100px auto',
    background: '#fff',
    padding: '24px',
    borderRadius: '14px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    margin: 0,
  },
  subtitle: {
    margin: '6px 0 0 0',
    color: '#666',
  },
  topRight: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  adminInfo: {
    fontSize: '14px',
    color: '#444',
  },
  logoutBtn: {
    padding: '10px 14px',
    border: 'none',
    borderRadius: '8px',
    background: '#dc2626',
    color: '#fff',
    cursor: 'pointer',
  },
  section: {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '18px',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '18px',
  },
  button: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: 'none',
    background: '#111827',
    color: '#fff',
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '14px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  success: {
    background: '#e7f8ec',
    color: '#166534',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '14px',
    fontSize: '14px',
  },
  error: {
    background: '#fde8e8',
    color: '#b91c1c',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '14px',
    fontSize: '14px',
  },
  pre: {
    background: '#111827',
    color: '#e5e7eb',
    padding: '16px',
    borderRadius: '10px',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
};

export default AdminRazorpayPanel;
